import React, { useState } from 'react';

const ChatGPTCaller = () => {
  const [prompt, setPrompt] = useState('What is the capital of France?');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingModels, setCheckingModels] = useState(false);
  const [checkingUsage, setCheckingUsage] = useState(false);
  const [error, setError] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('gpt-3.5-turbo');
  const [usageData, setUsageData] = useState(null);

  const checkAvailableModels = async () => {
    if (!apiKey) {
      setError('Please enter your OpenAI API key');
      return;
    }

    setCheckingModels(true);
    setError('');
    
    try {
      const response = await fetch('https://api.openai.com/v1/models', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to fetch models');
      }

      const data = await response.json();
      
      // Filter for only GPT models
      const gptModels = data.data
        .filter(model => 
          (model.id.includes('gpt') || model.id.includes('GPT')) && 
          !model.id.includes('instruct')
        )
        .map(model => model.id)
        .sort();
        
      setModels(gptModels);
      
      // Set default model to the best available one
      if (gptModels.includes('gpt-4')) {
        setSelectedModel('gpt-4');
      } else if (gptModels.includes('gpt-3.5-turbo')) {
        setSelectedModel('gpt-3.5-turbo');
      } else if (gptModels.length > 0) {
        setSelectedModel(gptModels[0]);
      }
      
    } catch (err) {
      setError(err.message || 'An error occurred while fetching available models');
    } finally {
      setCheckingModels(false);
    }
  };
  
  const checkUsage = async () => {
    if (!apiKey) {
      setError('Please enter your OpenAI API key');
      return;
    }

    setCheckingUsage(true);
    setError('');
    
    try {
      // Get the current date and first day of the current month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      // Format dates as ISO strings (YYYY-MM-DD)
      const startDate = startOfMonth.toISOString().split('T')[0];
      const endDate = now.toISOString().split('T')[0];
      
      const response = await fetch(`https://api.openai.com/v1/dashboard/billing/usage?start_date=${startDate}&end_date=${endDate}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to fetch usage data');
      }

      const usageResponse = await response.json();
      
      // Also get subscription data to show quota limits
      const subscriptionResponse = await fetch('https://api.openai.com/v1/dashboard/billing/subscription', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });
      
      if (!subscriptionResponse.ok) {
        const errorData = await subscriptionResponse.json();
        throw new Error(errorData.error?.message || 'Failed to fetch subscription data');
      }
      
      const subscriptionData = await subscriptionResponse.json();
      
      setUsageData({
        usage: usageResponse,
        subscription: subscriptionData
      });
      
    } catch (err) {
      setError(err.message || 'An error occurred while fetching usage data');
    } finally {
      setCheckingUsage(false);
    }
  };

  const makeApiCall = async () => {
    if (!apiKey) {
      setError('Please enter your OpenAI API key');
      return;
    }

    if (!selectedModel) {
      setError('Please select a model first');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: selectedModel,
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.7
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'API call failed');
      }

      const data = await response.json();
      setResponse(data.choices[0].message.content);
    } catch (err) {
      setError(err.message || 'An error occurred while making the API call');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto p-4">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-xl font-bold text-center text-gray-800">ChatGPT API Caller</h2>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">OpenAI API Key</label>
              <div className="flex mb-2">
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your OpenAI API key"
                  className="flex-1 p-2 border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={checkAvailableModels}
                  disabled={checkingModels || !apiKey}
                  className={`px-4 py-2 font-medium text-white ${
                    checkingModels || !apiKey ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50'
                  }`}
                >
                  {checkingModels ? 'Checking...' : 'Get Models'}
                </button>
                <button
                  onClick={checkUsage}
                  disabled={checkingUsage || !apiKey}
                  className={`px-4 py-2 font-medium text-white rounded-r ${
                    checkingUsage || !apiKey ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-500 hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50'
                  }`}
                >
                  {checkingUsage ? 'Checking...' : 'Check Usage'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Your API key is used only for these requests and not stored
              </p>
            </div>
            
            {models.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Model</label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {models.map(model => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {usageData && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Usage & Billing Information</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-600">Total usage this month:</span>
                    <span className="text-xs font-medium">${(usageData.usage.total_usage / 100).toFixed(2)} USD</span>
                  </div>
                  
                  {usageData.subscription.hard_limit_usd && (
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">Account limit:</span>
                      <span className="text-xs font-medium">${usageData.subscription.hard_limit_usd.toFixed(2)} USD</span>
                    </div>
                  )}
                  
                  {usageData.subscription.soft_limit_usd && (
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">Soft limit:</span>
                      <span className="text-xs font-medium">${usageData.subscription.soft_limit_usd.toFixed(2)} USD</span>
                    </div>
                  )}
                  
                  {usageData.subscription.system_hard_limit_usd && (
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">System hard limit:</span>
                      <span className="text-xs font-medium">${usageData.subscription.system_hard_limit_usd.toFixed(2)} USD</span>
                    </div>
                  )}
                  
                  {usageData.subscription.hard_limit_usd && (
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                      <div className="bg-blue-600 h-2.5 rounded-full" style={{ 
                        width: `${Math.min(100, (usageData.usage.total_usage / 100) / usageData.subscription.hard_limit_usd * 100)}%` 
                      }}></div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Enter your question"
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent h-20"
              />
            </div>
            
            <button 
              onClick={makeApiCall} 
              disabled={loading || models.length === 0}
              className={`w-full py-2 px-4 rounded font-medium text-white ${
                loading || models.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50'
              }`}
            >
              {loading ? 'Asking ChatGPT...' : 'Ask ChatGPT'}
            </button>
            
            {error && (
              <div className="p-3 bg-red-100 border-l-4 border-red-500 text-red-700">
                <p>{error}</p>
              </div>
            )}
            
            {response && (
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Response:</h3>
                <div className="p-3 bg-gray-100 rounded">
                  <p className="whitespace-pre-wrap text-gray-800">{response}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-3 bg-gray-50 text-xs text-gray-500">
          Note: This component requires a valid OpenAI API key to function. API usage may incur charges to your OpenAI account.
        </div>
      </div>
    </div>
  );
};

export default ChatGPTCaller;